import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql } from 'react-apollo';
import { compose, mapProps } from 'recompose';
import { withRouter } from 'react-router-dom';

import Button from 'components/common/Buttons/Button';
import ThreeppModal from 'components/common/Modal/Modal';
import { submitTemplateRecord as submitTemplateRecordMutation } from 'graphql/operations';
import { showSuccessMessage, showErrorMessage } from 'components/common/Alert/Alert';
import { getIdFromUrlParams } from 'selectors/assessment/selector';
import { getLanguageCodeFromId } from 'utils/functions';

import './SubmitModal.scss';

export const SubmitTemplateModal = props => {
  const { history, toggle, isOpen, submitTemplateRecord, urlGuid, templateLanguageId, templateLanguages } = props;
  const { t } = useTranslation();

  const close = useCallback(() => {
    toggle();
  }, [toggle]);

  const handleSubmitTemplate = useCallback(() => {
    const redirectToSuccessPage = () => {
      const langCode = getLanguageCodeFromId(templateLanguageId, templateLanguages);
      const newRecordSuccessBaseUrl = `/new-success/${urlGuid}`;
      langCode ? history.push(`${newRecordSuccessBaseUrl}/${langCode}`) : history.push(newRecordSuccessBaseUrl);
    };

    const redirectToTemplateDeletedPage = () => {
      history.push('/template-not-found');
    };

    submitTemplateRecord()
      .then(submitResponse => {
        const { isDeleted, isUpdated } = submitResponse.data.submitTemplateRecord.eventData.template;
        if (isUpdated) {
          showErrorMessage(t('newRecord.submit.templateUpdated'), { autoHideDuration: null });
        } else if (isDeleted) {
          showErrorMessage(t('newRecord.submit.templateDeleted'), { onCloseCallback: redirectToTemplateDeletedPage });
        } else {
          showSuccessMessage(t('assessment.submit.successMsg'), { onCloseCallback: redirectToSuccessPage });
        }
      })
      .catch(() => {
        showErrorMessage(t('assessment.submit.errorMsg'));
      });

    close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitTemplateRecord, close, history, urlGuid, t]);

  return (
    <>
      <ThreeppModal isOpen={isOpen} toggle={toggle} classes="submit-modal" header={t('assessment.submit.header')} size="sm">
        <div className="submit-content">{t('assessment.submit.confirmation')}</div>

        <div className="action-row">
          <Button testId="template-confirmationModal-cancel" className="btn-cancel" buttonStyle="destructive" onClick={close}>
            {t('common.cancel')}
          </Button>

          <Button testId="template-confirmationModal-submit" className="btn-submit" buttonStyle="primary" onClick={handleSubmitTemplate}>
            {t('common.submit')}
          </Button>
        </div>
      </ThreeppModal>
    </>
  );
};

export const SubmitModalHOC = compose(
  withRouter,
  graphql(submitTemplateRecordMutation, {
    options: props => {
      const { templateId, recordId } = props;
      const urlGuid = getIdFromUrlParams(props);
      return {
        variables: {
          templateId,
          recordId,
          urlGuid
        }
      };
    },
    name: 'submitTemplateRecord'
  }),
  mapProps(props => {
    const { toggle, isOpen, submitTemplateRecord, history, templateLanguageId, templateLanguages } = props;
    return {
      history,
      toggle,
      isOpen,
      submitTemplateRecord,
      templateLanguageId,
      templateLanguages,
      urlGuid: getIdFromUrlParams(props)
    };
  })
);

export default SubmitModalHOC(SubmitTemplateModal);
